import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  EyeIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi } from '../../api/marketplace';
import { speciesApi, Species } from '../../api/species';
import { IUCNStatus } from '../../utils/enums';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalConfig } from '../../contexts/GlobalConfigContext';
import toast from 'react-hot-toast';

interface AnalyticsData {
  totalListings: number;
  activeListings: number;
  soldListings: number;
  totalRevenue: number;
  averagePrice: number;
  totalViews: number;
  totalInquiries: number;
  conversionRate: number;
  speciesBreakdown: {
    species: string;
    count: number;
    revenue: number;
    averagePrice: number;
  }[];
  topPerformingSpecies: {
    species: string;
    views: number;
    inquiries: number;
    sales: number;
    revenue: number;
  }[];
  conservationStatus: {
    status: string;
    count: number;
    percentage: number;
  }[];
  geographicDistribution: {
    origin: string;
    count: number;
    percentage: number;
  }[];
}

export default function AdvancedAnalytics() {
  const { user } = useAuth();
  const { config } = useGlobalConfig();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [speciesData, setSpeciesData] = useState<Species[]>([]);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    loadSpeciesData();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, user?.facilityId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const facilityId = user?.facilityId || 'default';
      const response = await marketplaceApi.getAnalytics(facilityId, { timeRange });
      setAnalytics(response);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadSpeciesData = async () => {
    try {
      const response = await speciesApi.getAll();
      setSpeciesData(response.data || []);
    } catch (error) {
      console.error('Failed to load species data:', error);
    }
  };

  const getSpeciesInfo = (speciesName: string) => {
    return speciesData.find((species) => species.commonName === speciesName);
  };

  const getIUCNStatusDisplay = (status: IUCNStatus) => {
    switch (status) {
      case IUCNStatus.LEAST_CONCERN:
        return 'Least Concern';
      case IUCNStatus.NEAR_THREATENED:
        return 'Near Threatened';
      case IUCNStatus.VULNERABLE:
        return 'Vulnerable';
      case IUCNStatus.ENDANGERED:
        return 'Endangered';
      case IUCNStatus.CRITICALLY_ENDANGERED:
        return 'Critically Endangered';
      case IUCNStatus.EXTINCT_IN_WILD:
        return 'Extinct in Wild';
      case IUCNStatus.EXTINCT:
        return 'Extinct';
      case IUCNStatus.DATA_DEFICIENT:
        return 'Data Deficient';
      case IUCNStatus.NOT_EVALUATED:
        return 'Not Evaluated';
      default:
        return status;
    }
  };

  const getIUCNStatusColor = (status: IUCNStatus) => {
    switch (status) {
      case IUCNStatus.LEAST_CONCERN:
        return 'bg-emerald-100 text-emerald-800';
      case IUCNStatus.NEAR_THREATENED:
        return 'bg-amber-100 text-amber-800';
      case IUCNStatus.VULNERABLE:
        return 'bg-orange-100 text-orange-800';
      case IUCNStatus.ENDANGERED:
      case IUCNStatus.CRITICALLY_ENDANGERED:
        return 'bg-rose-100 text-rose-800';
      case IUCNStatus.EXTINCT:
      case IUCNStatus.EXTINCT_IN_WILD:
        return 'bg-violet-100 text-violet-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config?.currency?.default || 'ZAR',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Advanced Analytics</h2>
          <p className="text-sm text-slate-500">Comprehensive marketplace insights with species data</p>
        </div>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="1y">Last year</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-cyan-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-500">Total Listings</p>
              <p className="text-2xl font-bold text-slate-900">{analytics.totalListings || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-500">Total Revenue</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(analytics.totalRevenue || 0)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <EyeIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-500">Total Views</p>
              <p className="text-2xl font-bold text-slate-900">{(analytics.totalViews || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ArrowTrendingUpIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-slate-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-slate-900">{(analytics.conversionRate || 0).toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Top Performing Species</h3>
          <div className="space-y-4">
            {analytics.topPerformingSpecies?.length ? (
              analytics.topPerformingSpecies.map((species, index) => {
                const speciesInfo = getSpeciesInfo(species.species);
                return (
                  <div key={species.species} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                        <span className="text-sm font-medium text-cyan-600">{index + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{species.species}</p>
                        {speciesInfo && (
                          <div className="flex items-center space-x-2 text-xs text-slate-500">
                            <span>{speciesInfo.genus} {speciesInfo.species}</span>
                            <span>•</span>
                            <span>{speciesInfo.originLocation}</span>
                            <span>•</span>
                            <span className={`px-1 py-0.5 rounded text-xs ${getIUCNStatusColor(speciesInfo.iucnStatus)}`}>
                              {getIUCNStatusDisplay(speciesInfo.iucnStatus)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">{formatCurrency(species.revenue)}</p>
                      <p className="text-sm text-slate-500">{species.sales} sales</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-slate-500">No species performance data for this period.</p>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Conservation Status Distribution</h3>
          <div className="space-y-3">
            {analytics.conservationStatus?.length ? (
              analytics.conservationStatus.map((status) => (
                <div key={status.status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      status.status === 'Least Concern' ? 'bg-green-500' :
                      status.status === 'Near Threatened' ? 'bg-yellow-500' :
                      status.status === 'Vulnerable' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`}></div>
                    <span className="text-sm font-medium text-slate-700">{status.status}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-slate-900">{status.count}</span>
                    <span className="text-sm text-slate-500">({status.percentage}%)</span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No conservation data available.</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Geographic Distribution</h3>
        {analytics.geographicDistribution?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {analytics.geographicDistribution.map((region) => (
              <div key={region.origin} className="text-center p-4 bg-slate-50/80 rounded-lg">
                <GlobeAltIcon className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <p className="font-medium text-slate-900">{region.origin}</p>
                <p className="text-2xl font-bold text-cyan-600">{region.count}</p>
                <p className="text-sm text-slate-500">{region.percentage}%</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No geographic distribution data available.</p>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Species Market Insights</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Species</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Scientific Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Origin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">IUCN Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Market Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Demand Level</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {speciesData.slice(0, 10).map((species) => (
                <tr key={species.id} className="hover:bg-slate-50/80">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">{species.commonName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-500 italic">{species.genus} {species.species}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-500">{species.originLocation}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getIUCNStatusColor(species.iucnStatus)}`}>
                      {getIUCNStatusDisplay(species.iucnStatus)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900">
                      {species.priceRangeMin && species.priceRangeMax ?
                        `${formatCurrency(species.priceRangeMin)} - ${formatCurrency(species.priceRangeMax)}` :
                        species.marketValueRange || 'Not specified'
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-500">{species.marketDemandLevel || 'Not specified'}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
